import { NgClass, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ContentChild, Directive, Input, OnInit, TemplateRef, inject } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { InputBase } from '../input-base/input-base';
import { DOMEvent } from '../input-base/input-base.model';
import { InputLabelComponent } from '../input-base/input-label/input-label.component';

@Directive({
  selector: '[radioDescription]'
})
export class RadioDescription {
  template = inject<TemplateRef<any>>(TemplateRef);

  @Input() hide = false;
}

/**
 * Component that can be presented as `checkbox` or `radio-input`; based on `type` parameter
 */
@Component({
  selector: 'app-radio,mbs-radio',
  imports: [InputLabelComponent, NgClass, NgTemplateOutlet],
  templateUrl: './radio.component.html'
})
export class RadioComponent<T> extends InputBase<T> implements ControlValueAccessor, OnInit, AfterViewInit {
  protected cd: ChangeDetectorRef;

  @Input() subtitle: string;
  @Input() subtitleClasses: string;
  @Input() subtitleHide = false;
  @Input() subtitleOutside = true;

  @Input() descriptionOutside = true;
  @Input() descriptionClasses: string;

  @Input() centerRadio = true;

  /**
   * Add font-weight-bold;
   */
  @Input() public boldLabel = false;

  @Input() public set value(value: T) {
    this.myValue = value;
    this.myChecked = this.modelValue === this.value;
  }
  public get value(): T {
    return this.myValue;
  }
  protected labelContentClassesDefault = 'mbs-radio_label-content';
  private get modelValue(): T {
    return this.ngControl.value as T;
  }

  protected myChecked = false;
  /**
   * context for label template
   */
  @Input() labelContext: any;

  /**
   * Native checked radio button
   * @param {boolean} state
   */
  @Input() public set checked(state: boolean) {
    this.myChecked = state;
    if (this.onChange) {
      this.onChange(this.value);
    }
    this.change.emit(this.value);
  }
  public get checked(): boolean {
    return this.myChecked;
  }

  @Input() labelClasses = '';

  @ContentChild(RadioDescription, { static: true, read: RadioDescription }) radioDescription: RadioDescription;

  constructor() {
    const ngControl = inject(NgControl, { optional: true, self: true });
    const cd = inject(ChangeDetectorRef);

    super();

    this.cd = cd;
  }

  ngAfterViewInit(): void {
    this.ngControl?.valueChanges?.subscribe({
      next: (val) => this.writeValue(val)
    });
  }

  /**
   * Default change method. Can be replaced.
   * @param {DOMEvent<HTMLInputElement>}event - native DOM input event
   */
  handleChange(event: DOMEvent<HTMLInputElement>): void {
    event.stopPropagation();
    this.checked = event.target.checked;
    this.onTouched && this.onTouched();
  }

  writeValue(obj: any): void {
    this.myChecked = obj === this.value;
    this.cd.markForCheck();
  }

  ngOnInit(): void {
    // Unnamed radios would all land in one native group "undefined" and uncheck each other across unrelated controls
    this.name ??= this.id;

    if (this.label && !this.id) {
      console.error(`Radio id must be specified if label in use`);
    }
    if (this.value === null || this.value === undefined) {
      console.error('Radio value must be specified');
    }
  }
}
